/**
 * @file Phone auth action.
 */

import { isValidPhone, parsePhone } from '../util/phone';
import getLogger from '../util/logger';
import { getErrorString } from '../util/normalize-errors';
import {
  FAILED_TO_FETCH_ERROR_MESSAGE,
  FAILED_TO_FETCH_ERROR_REPLACEMENT,
} from '../constants/error-messages';

const log = getLogger('PhoneAuthAction');

export const PHONE_AUTH_SUBMIT_REQUEST = 'auth/PHONE_SUBMIT_REQUEST';
export const PHONE_AUTH_SUBMIT_SUCCESS = 'auth/PHONE_SUBMIT_SUCCESS';
export const PHONE_AUTH_SUBMIT_FAILURE = 'auth/PHONE_SUBMIT_FAILURE';
export const PHONE_AUTH_SUBMIT_CAPTCHA_FAILURE =
  'auth/PHONE_SUBMIT_CAPTCHA_FAILURE';
export const PHONE_AUTH_SUBMIT_CAPTCHA_BLOCK =
  'auth/PHONE_AUTH_SUBMIT_CAPTCHA_BLOCK';

export const PHONE_AUTH_VERIFY_REQUEST = 'auth/PHONE_VERIFY_REQUEST';
export const PHONE_AUTH_VERIFY_SUCCESS = 'auth/PHONE_VERIFY_SUCCESS';
export const PHONE_AUTH_VERIFY_FAILURE = 'auth/PHONE_VERIFY_FAILURE';

export const PHONE_AUTH_CODE_RESEND_REQUEST =
  'auth/PHONE_AUTH_CODE_RESEND_REQUEST';
export const PHONE_AUTH_CODE_RESEND_SUCCESS =
  'auth/PHONE_AUTH_CODE_RESEND_SUCCESS';
export const PHONE_AUTH_CODE_RESEND_FAILURE =
  'auth/PHONE_AUTH_CODE_RESEND_FAILURE';
export const PHONE_AUTH_CODE_RESEND_CAPTCHA_FAILURE =
  'auth/PHONE_AUTH_CODE_RESEND_CAPTCHA_FAILURE';
export const PHONE_AUTH_CODE_RESEND_CAPTCHA_BLOCK =
  'auth/PHONE_AUTH_CODE_RESEND_CAPTCHA_BLOCK';

const phoneAuthSubmitRequest = () => {
  return { type: PHONE_AUTH_SUBMIT_REQUEST };
};

const phoneAuthSubmitSuccess = () => {
  return { type: PHONE_AUTH_SUBMIT_SUCCESS };
};

const phoneAuthSubmitFailure = error => {
  return { type: PHONE_AUTH_SUBMIT_FAILURE, error };
};

const phoneAuthSubmitCaptchaFailure = () => {
  return { type: PHONE_AUTH_SUBMIT_CAPTCHA_FAILURE };
};

const phoneAuthSubmitCaptchaBlock = () => {
  return { type: PHONE_AUTH_SUBMIT_CAPTCHA_BLOCK };
};

const phoneAuthVerifyRequest = () => {
  return { type: PHONE_AUTH_VERIFY_REQUEST };
};

const phoneAuthVerifySuccess = () => {
  return { type: PHONE_AUTH_VERIFY_SUCCESS };
};

const phoneAuthVerifyFailure = error => {
  return { type: PHONE_AUTH_VERIFY_FAILURE, error };
};

const phoneAuthCodeResendRequest = () => {
  return { type: PHONE_AUTH_CODE_RESEND_REQUEST };
};

const phoneAuthCodeResendSuccess = () => {
  return { type: PHONE_AUTH_CODE_RESEND_SUCCESS };
};

const phoneAuthCodeResendFailure = error => {
  return { type: PHONE_AUTH_CODE_RESEND_FAILURE, error };
};

const phoneAuthCodeResendCaptchaFailure = () => {
  return { type: PHONE_AUTH_CODE_RESEND_CAPTCHA_FAILURE };
};

const phoneAuthCodeResendCaptchaBlock = () => {
  return { type: PHONE_AUTH_CODE_RESEND_CAPTCHA_BLOCK };
};

export const phoneAuthSubmit = (
  phoneVerificationRequest,
  phone,
  rethrow,
  captchaToken = undefined,
  forceAllowAction = false,
) => async dispatch => {
  log.info('phoneAuthSubmit() :: Requesting phone auth for:', phone);

  dispatch(phoneAuthSubmitRequest());

  const parsedPhone = parsePhone(phone);

  if (!isValidPhone(parsedPhone)) {
    dispatch(phoneAuthSubmitFailure('Invalid phone number'));

    if (rethrow) {
      throw Error('Invalid phone number');
    }

    return;
  }

  try {
    const {
      data: { phoneVerificationRequest: payload } = {},
    } = await phoneVerificationRequest({
      phoneNumber: parsedPhone,
      captchaToken,
      forceAllowAction,
    });

    if (!!payload.captcha_result && payload.captcha_result?.challenge_user) {
      dispatch(phoneAuthSubmitCaptchaFailure());
      return;
    }

    if (
      !!payload.captcha_result &&
      !payload.captcha_result.challenge_user &&
      !payload.captcha_result.allow_action
    ) {
      dispatch(phoneAuthSubmitCaptchaBlock());
      if (rethrow) {
        throw Error('Could not verify user authenticity');
      }
      return;
    }

    if (payload.success) {
      dispatch(phoneAuthSubmitSuccess());
    }

    log.info('phoneAuthSubmit() :: Success');
  } catch (error) {
    log.error('phoneAuthSubmit() :: Error:', error);

    let message =
      getErrorString(error) ??
      'An unexpected error has occurred, please try again';

    if (message === FAILED_TO_FETCH_ERROR_MESSAGE) {
      message = FAILED_TO_FETCH_ERROR_REPLACEMENT;
    }

    dispatch(phoneAuthSubmitFailure(message));
    if (rethrow) {
      throw Error(message);
    }
  }
};

export const phoneAuthVerify = (
  accountLoginByPhone,
  phone,
  code,
  onLogin,
) => async dispatch => {
  log.info('phoneAuthVerify() :: Phone auth verification...');

  dispatch(phoneAuthVerifyRequest());

  if (code.length < 4) {
    dispatch(phoneAuthVerifyFailure('Invalid code'));
    return;
  }

  try {
    await accountLoginByPhone({ phoneNumber: phone, code });

    dispatch(phoneAuthVerifySuccess());

    log.info('phoneAuthVerify() :: Success');

    if (onLogin) {
      onLogin();
    }
  } catch (error) {
    log.error('phoneAuthVerify() :: Error:', error);

    let message =
      getErrorString(error) ??
      'An unexpected error has occurred, please try again';

    if (message === FAILED_TO_FETCH_ERROR_MESSAGE) {
      message = FAILED_TO_FETCH_ERROR_REPLACEMENT;
    }

    dispatch(phoneAuthVerifyFailure(message));
  }
};

export const resendPhoneAuthCode = (
  phoneVerificationRequest,
  phone,
  rethrow,
  captchaToken = undefined,
  forceAllowAction = false,
) => async dispatch => {
  log.info('resendPhoneAuthCode() :: Resending phone auth code for:', phone);

  dispatch(phoneAuthCodeResendRequest());

  const parsedPhone = parsePhone(phone);

  if (!isValidPhone(parsedPhone)) {
    dispatch(phoneAuthCodeResendFailure('Invalid phone number'));

    if (rethrow) {
      throw Error('Invalid phone number');
    }

    return;
  }

  try {
    const {
      data: { phoneVerificationRequest: payload } = {},
    } = await phoneVerificationRequest({
      phoneNumber: parsedPhone,
      captchaToken,
      forceAllowAction,
    });

    if (!!payload.captcha_result && payload.captcha_result?.challenge_user) {
      dispatch(phoneAuthCodeResendCaptchaFailure());
      return;
    }

    if (
      !!payload.captcha_result &&
      !payload.captcha_result.challenge_user &&
      !payload.captcha_result.allow_action
    ) {
      dispatch(phoneAuthCodeResendCaptchaBlock());
      if (rethrow) {
        throw Error('Could not verify user authenticity');
      }
      return;
    }

    if (payload.success) {
      dispatch(phoneAuthCodeResendSuccess());
    }

    log.info('resendPhoneAuthCode() :: Success');
  } catch (error) {
    log.error('resendPhoneAuthCode() :: Error:', error);

    let message =
      getErrorString(error) ??
      'An unexpected error has occurred, please try again';

    if (message === FAILED_TO_FETCH_ERROR_MESSAGE) {
      message = FAILED_TO_FETCH_ERROR_REPLACEMENT;
    }

    dispatch(phoneAuthCodeResendFailure(message));
    if (rethrow) {
      throw Error(message);
    }
  }
};
